import { useCallback, useState } from 'react';

import {
  type SchoolItem,
  SchoolSearchTree,
} from '../../../../../../shared/components/school-search-tree/school-search-tree';
import { useTrackFilterReset } from '../../../hooks/useTrackFilterReset';
import type { SchoolFilter } from '../../../types/filters';
import { FilterSelectFooter } from '../../FilterSelectFooter';
import layout from '../filterFormLayout.module.css';

export interface SchoolSearchFilterFormProps {
  treeHeight?: number;
  onClose: () => void;
  filter: SchoolFilter;
  onSave: (filter: SchoolFilter) => void;
  isNewInstance?: boolean;
}

export const SchoolSearchFilterForm = ({
  treeHeight = 220,
  onClose,
  filter,
  onSave,
  isNewInstance,
}: SchoolSearchFilterFormProps) => {
  const defaultSelectedItems: Record<string, SchoolItem> =
    filter && 'schoolResultItems' in filter ? filter.schoolResultItems : {};

  const [selections, setSelections] =
    useState<Record<string, SchoolItem>>(defaultSelectedItems);
  const trackFilterReset = useTrackFilterReset();

  const onClearSelections = useCallback(() => {
    trackFilterReset(filter, {
      selectionCountBefore: Object.keys(selections).length,
    });
    setSelections({});
  }, [filter, selections, trackFilterReset]);

  const handleAddFilter = useCallback(() => {
    if (Object.keys(selections).length > 0) {
      // Omit isNot so FilterFormRender.handleSaveFilter uses the centralized isNotEnabled state
      const { isNot: _omit, ...filterWithoutIsNot } = filter;
      onSave({
        ...filterWithoutIsNot,
        schoolResultItems: selections,
        isUserSubmitted: true,
      });
    }
    // If no selections and it's a new instance, just close without saving
    // If it's an existing instance, the parent should handle removal
    onClose();
  }, [selections, filter, onSave, onClose]);

  const hasExistingSelections = Object.keys(defaultSelectedItems).length > 0;

  return (
    <div className={layout.formStack}>
      <div>
        <SchoolSearchTree
          selections={selections}
          setSelections={setSelections}
          treeHeight={treeHeight}
        />
      </div>
      <div>
        <FilterSelectFooter
          onClearSelections={onClearSelections}
          onClose={onClose}
          onAdd={handleAddFilter}
          addLabel={isNewInstance || !hasExistingSelections ? 'Add' : 'Update'}
          isAddDisabled={Object.keys(selections).length === 0}
        />
      </div>
    </div>
  );
};
